import { ModbusError, ModbusErrorCode } from './modbus.multi-request';

const RP_DEBUG = { ENABLED: true };
const rpLog = (msg: string) => RP_DEBUG.ENABLED && console.log(`[RetryPolicy] ${msg}`);

// ─── Retry Policy ─────────────────────────────────────────────────────────────
//
// Configurable retry strategy for the CommandScheduler.
// Used as configuration + decision helper — the scheduler owns the loop,
// the policy decides "should I retry?" and "how long to wait?".
//
// Channel-specific presets are selected by ModbusLinkOptions.retryPolicy.
// The scheduler falls back to DEFAULT_RETRY_POLICY if none is provided.
// Classification: RequestFailed/BusError → never retry, Timeout/decode → retry,
// protocol exceptions (0x01-0x03) → never retry (won't change on retry).

export interface RetryPolicyConfig {
  /** Maximum number of retry attempts (not counting the initial try). Default: 3 */
  maxRetries: number;
  /** Base delay in ms before first retry. Default: 500 */
  baseDelayMs: number;
  /** Maximum delay in ms (caps exponential growth). Default: 10000 */
  maxDelayMs: number;
  /** Multiplier for exponential backoff. Default: 2 */
  backoffMultiplier: number;
}

const DEFAULT_RETRY_CONFIG: RetryPolicyConfig = {
  maxRetries: 3,
  baseDelayMs: 500,
  maxDelayMs: 10_000,
  backoffMultiplier: 2,
};

export class RetryPolicy {
  readonly config: RetryPolicyConfig;

  constructor(config?: Partial<RetryPolicyConfig>) {
    this.config = { ...DEFAULT_RETRY_CONFIG, ...config };
  }

  get maxRetries(): number {
    return this.config.maxRetries;
  }

  /**
   * Whether a ModbusError should be retried at the given attempt number.
   * Attempt is 1-based (1 = first retry after initial failure).
   *
   * Classification rules:
   * - RequestFailed → NEVER retry (transport dead, circuit breaker handles it)
   * - BusError0500 → NEVER retry (will halve batch instead)
   * - Timeout → YES retry (transient, may recover)
   * - SlaveDeviceFailure (G3) → YES retry (device busy, may recover)
   * - IllegalFunction/DataAddress/DataValue (G3) → NEVER retry (won't change)
   * - Legacy Modbus exceptions → NEVER retry (fatal in legacy mode)
   * - Decode errors → YES retry (may be transient corruption)
   */
  shouldRetry(err: ModbusError, attempt: number, isG3: boolean): boolean {
    if (attempt > this.config.maxRetries) {
      return false;
    }

    switch (err.code) {
      // Transport dead — no point retrying
      case ModbusErrorCode.RequestFailed:
        return false;

      // Bus error — scheduler will halve batch, not retry same size
      case ModbusErrorCode.BusError0500:
        return false;

      // Timeout — transient, worth retrying
      case ModbusErrorCode.Timeout:
        return true;

      // G3: SlaveDeviceFailure — device busy, retry with backoff
      case ModbusErrorCode.SlaveDeviceFailure:
        return isG3;

      // 0x06: device is busy processing — always retry (transient)
      case ModbusErrorCode.SlaveDeviceBusy:
        return true;

      // Protocol errors — won't change on retry
      case ModbusErrorCode.IllegalFunction:
      case ModbusErrorCode.IllegalDataAddress:
      case ModbusErrorCode.IllegalDataValue:
        return false;

      // Gateway errors — transport dead, no point retrying
      case ModbusErrorCode.GatewayPathUnavailable:
      case ModbusErrorCode.GatewayTargetFailed:
        return false;

      // Legacy catch-all Modbus exception
      case ModbusErrorCode.ModbusError:
        return false;

      // Decode errors (201-207) — may be transient corruption
      default:
        return true;
    }
  }

  /**
   * Calculate delay in ms before the Nth retry attempt.
   * Uses exponential backoff: baseDelay * multiplier^(attempt-1), capped at maxDelay.
   */
  getDelayMs(attempt: number): number {
    const delay = this.config.baseDelayMs * Math.pow(this.config.backoffMultiplier, attempt - 1);
    const capped = Math.min(delay, this.config.maxDelayMs);
    rpLog(`attempt ${attempt}: delay=${capped}ms (base=${this.config.baseDelayMs}, mult=${this.config.backoffMultiplier})`);
    return capped;
  }
}

// ─── Preset Policies ──────────────────────────────────────────────────────────
//
// Each channel type has different latency and reliability characteristics.
// These presets are selected by the scheduler based on the channel's concurrentQuery.

/** Internet/Cloud channels — high latency, few retries with long delays. */
export const INTERNET_RETRY_POLICY = new RetryPolicy({
  maxRetries: 2,
  baseDelayMs: 1000,
  maxDelayMs: 5000,
  backoffMultiplier: 2,
});

/** LAN channels — medium latency, moderate retries. */
export const LAN_RETRY_POLICY = new RetryPolicy({
  maxRetries: 3,
  baseDelayMs: 500,
  maxDelayMs: 3000,
  backoffMultiplier: 2,
});

/** BLE channels — low latency, fast retries. */
export const BLE_RETRY_POLICY = new RetryPolicy({
  maxRetries: 2,
  baseDelayMs: 300,
  maxDelayMs: 1500,
  backoffMultiplier: 2,
});

/** Default fallback (same as old DECODE_MAX_RETRIES=5 behavior). */
export const DEFAULT_RETRY_POLICY = new RetryPolicy({
  maxRetries: 5,
  baseDelayMs: 500,
  maxDelayMs: 5000,
  backoffMultiplier: 1.5,
});
